import { useMemo } from "react";
import { HistoryTxType } from "../../core/types/history";
import {
  SwapTransactionForStatus,
  TransactionStatus,
} from "../../core/types/transaction";
import { isHistoryTransactionEnded } from "../../services";
import { useHistoryStore } from "../store/useHistoryStore";
import { useHistory } from "../user/useHistory";
import { useSwapStatusQuery } from "./useSwapStatusQuery";

interface UseSwapTransactionStatusParams {
  transaction?: SwapTransactionForStatus;
  retry?: number;
  refetchOnWindowFocus?: "always" | boolean;
  enabled?: boolean;
}

/**
 * Fetch status of a Swap transaction
 */
export const useSwapTransactionStatus = ({
  transaction,
  retry = 25,
  refetchOnWindowFocus = "always",
  enabled = true,
}: UseSwapTransactionStatusParams) => {
  const { replaceSwapTransactionStatus } = useHistory();
  const findTransaction = useHistoryStore((state) => state.findTransaction);

  const currentHistoryItem = useMemo(
    () =>
      findTransaction({
        transactionId: transaction?.transactionId,
        txType: HistoryTxType.SWAP,
      }),
    [findTransaction, transaction?.transactionId]
  );

  const transactionStatusQuery = useSwapStatusQuery({
    transaction,
    retry,
    refetchOnWindowFocus,
    enabled:
      enabled &&
      !!currentHistoryItem &&
      !isHistoryTransactionEnded({
        data: currentHistoryItem?.data,
        txType: HistoryTxType.SWAP,
      }),
    onEndStatus: ({ status, statusResponse }) => {
      if (!transaction?.transactionId) return;

      replaceSwapTransactionStatus({
        transactionId: transaction.transactionId,
        statusResponse,
        status,
      });
    },
    onNotFound: () => {
      if (!transaction?.transactionId) return;

      replaceSwapTransactionStatus({
        transactionId: transaction.transactionId,
        statusResponse: undefined,
        status: TransactionStatus.NOT_FOUND,
      });
    },
    onError: () => {
      if (!transaction?.transactionId) return;

      replaceSwapTransactionStatus({
        transactionId: transaction.transactionId,
        statusResponse: undefined,
        status: TransactionStatus.ERROR,
      });
    },
  }).transactionStatusQuery;

  return {
    transactionStatusQuery,
    latestStatus:
      (transactionStatusQuery.data
        ?.squidTransactionStatus as any as TransactionStatus) ??
      transaction?.status ??
      currentHistoryItem?.data?.status,
  };
};
